import { DefaultChatTransport, type UIMessage, type UIMessageChunk } from "ai"

import { resolveApiUrl } from "@/hooks/api/base"

export interface TwoStepChatTransportOptions {
  threadId: string
  getContext: () => Promise<{ token?: string; bookId?: string }>
  buildBody: (input: {
    messages: unknown[]
    body: Record<string, unknown> | undefined
    trigger: "submit-message" | "regenerate-message"
    messageId: string | undefined
  }) => Record<string, unknown>
  buildFetchInit: (init: RequestInit | undefined, context: { token?: string; bookId?: string }) => RequestInit
}

const STREAM_BASE_PATH = "/api/chat-streams"

export class ChatRequestNotAcceptedError extends Error {}

/**
 * Two-step transport: POST to Rails to create the message + get a stream_id,
 * then GET the Next.js handler to stream chunks from Upstash.
 *
 * The Vercel AI SDK's DefaultChatTransport assumes POST -> SSE in one step,
 * which doesn't match our backend split. We subclass it so we can keep its
 * processResponseStream parser (handles the v1 UI message stream format).
 */
export class TwoStepChatTransport<UI_MESSAGE extends UIMessage> extends DefaultChatTransport<UI_MESSAGE> {
  private threadId: string
  private getContext: TwoStepChatTransportOptions["getContext"]
  private buildBody: TwoStepChatTransportOptions["buildBody"]
  private buildFetchInit: TwoStepChatTransportOptions["buildFetchInit"]

  constructor(options: TwoStepChatTransportOptions) {
    super({})
    this.threadId = options.threadId
    this.getContext = options.getContext
    this.buildBody = options.buildBody
    this.buildFetchInit = options.buildFetchInit
  }

  async sendMessages(
    options: Parameters<DefaultChatTransport<UI_MESSAGE>["sendMessages"]>[0]
  ): Promise<ReadableStream<UIMessageChunk>> {
    const context = await this.getContext()
    const body = this.buildBody({
      messages: options.messages,
      body: options.body as Record<string, unknown> | undefined,
      trigger: options.trigger,
      messageId: options.messageId,
    })

    const url = resolveApiUrl(`chat_threads/${this.threadId}/chat_responses`)
    if (context.bookId) url.searchParams.set("book_id", context.bookId)

    const init = this.buildFetchInit(
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal: options.abortSignal,
      },
      context
    )

    const railsResponse = await fetch(url.toString(), init)
    if (!railsResponse.ok) {
      throw new ChatRequestNotAcceptedError((await railsResponse.text()) || "Failed to start chat response")
    }

    const { stream_id: streamId } = (await railsResponse.json()) as { stream_id: string }
    return this.openStream(streamId, context.bookId, options.abortSignal)
  }

  async reconnectToStream(
    options: Parameters<DefaultChatTransport<UI_MESSAGE>["reconnectToStream"]>[0]
  ): Promise<ReadableStream<UIMessageChunk> | null> {
    const context = await this.getContext()

    const url = resolveApiUrl(`chat_threads/${this.threadId}/chat_stream`)
    if (context.bookId) url.searchParams.set("book_id", context.bookId)

    const init = this.buildFetchInit({ method: "GET" }, context)

    const railsResponse = await fetch(url.toString(), init)
    if (railsResponse.status === 204) return null
    if (!railsResponse.ok) {
      throw new Error((await railsResponse.text()) || "Failed to look up active chat stream")
    }

    const { stream_id: streamId } = (await railsResponse.json()) as { stream_id: string }
    if (!streamId) return null

    return this.openStream(streamId, context.bookId, undefined)
  }

  private async openStream(
    streamId: string,
    bookId: string | undefined,
    abortSignal: AbortSignal | undefined
  ): Promise<ReadableStream<UIMessageChunk>> {
    const streamUrl = new URL(`${STREAM_BASE_PATH}/${streamId}`, window.location.origin)
    streamUrl.searchParams.set("thread_id", this.threadId)
    if (bookId) streamUrl.searchParams.set("book_id", bookId)

    const response = await fetch(streamUrl.toString(), { method: "GET", signal: abortSignal })

    if (!response.ok) throw new Error((await response.text()) || "Failed to open chat stream")
    if (!response.body) throw new Error("Chat stream response body is empty")

    return this.processResponseStream(response.body)
  }
}
